'use client';

import { AmountRow } from '@insta-club/components/common';
import { Invoice } from '@insta-club/models';
import { useSessionStore } from '@insta-club/state';
import { t } from '@insta-club/translations';
import { formatDate, separator } from '@insta-club/utilities';
import { useEffect, useMemo } from 'react';

interface Props {
    invoice: Invoice;
    useWallet: boolean;
    discountCodeAmount: number;
    mustPayOnline: (amount: number) => void;
}

const PaymentDetails: React.FC<Props> = ({ invoice, discountCodeAmount, useWallet, mustPayOnline }) => {
    const title = invoice?.items?.[0]?.name;
    const userBalance = useSessionStore((state) => state.session?.balance) ?? 0;
    const payableAmount = invoice.amount - discountCodeAmount;

    const showAmounts = useMemo(
        () =>
            invoice?.items?.reduce((sum, { show_amount, amount }) => {
                return sum + (show_amount || amount);
            }, 0),
        [invoice?.items]
    );

    const profit = useMemo(
        () =>
            invoice?.items?.reduce((sum, { amount, show_amount }) => {
                return sum + (show_amount - amount);
            }, 0) + discountCodeAmount,
        [discountCodeAmount, invoice?.items]
    );

    const [payWallet, payOnline] = useMemo(() => {
        if (!useWallet) return [0, payableAmount];
        const walletPay = Math.min(userBalance, payableAmount);
        const onlinePay = payableAmount - walletPay;
        return [walletPay, onlinePay];
    }, [payableAmount, userBalance, useWallet]);

    useEffect(() => {
        mustPayOnline(payOnline);
    }, [payOnline, mustPayOnline]);

    return (
        <div className='rounded-xl bg-gray-100 p-4'>
            {title ? (
                <h4 className='mb-4 text-base font-semibold text-gray-700'>{t('buy_subscription', title)}</h4>
            ) : null}
            <ul className='divide-y divide-gray-200'>
                <AmountRow title={t('order_amount')} value={separator(showAmounts)} />
                {profit > 0 ? <AmountRow title={t('special_discount')} value={separator(profit)} /> : null}
                {discountCodeAmount ? (
                    <AmountRow title={t('discount_code')} value={separator(discountCodeAmount)} />
                ) : null}
                {invoice.status == 'paid' ? (
                    <AmountRow title={t('registration_date')} value={formatDate(invoice.created_at)} price={false} />
                ) : null}
                {invoice.status == 'paid' ? (
                    <AmountRow title={t('payment_date')} value={formatDate(invoice?.updated_at)} price={false} />
                ) : null}
                {payWallet ? <AmountRow title={t('pay_with_wallet')} value={separator(payWallet)} /> : null}
                {payOnline ? <AmountRow title={t('online_payment')} value={separator(payOnline)} /> : null}
                <AmountRow title={t('total_payment')} value={separator(payableAmount)} />
            </ul>
        </div>
    );
};

export default PaymentDetails;

